import React,{useEffect,useState} from 'react';
import {FiArrowLeft,FiUser,FiCreditCard,FiShield,FiExternalLink,FiCheckCircle,FiAlertCircle,FiRefreshCw} from 'react-icons/fi';
import supabase from '../supabase/supabase';
import './AccountPage.css';

const defaultBilling={configured:false,plan:'beta',status:'not_subscribed',hasCustomer:false,currentPeriodEnd:null,cancelAtPeriodEnd:false};
const statuses={active:'Active',trialing:'Trial',past_due:'Payment overdue',unpaid:'Payment required',canceled:'Canceled',incomplete:'Incomplete',incomplete_expired:'Expired',paused:'Paused',not_subscribed:'No paid subscription'};

export default function AccountPage({session,onBack,onSignIn,requestPasswordReset}){
 const user=session?.user;
 const [tab,setTab]=useState('profile'),[name,setName]=useState(''),[saving,setSaving]=useState(false);
 const [billing,setBilling]=useState(defaultBilling),[busy,setBusy]=useState(false),[loading,setLoading]=useState(true);
 const [notice,setNotice]=useState(''),[error,setError]=useState('');
 useEffect(()=>{
   if(!user?.id)return;
   let active=true;
   supabase.from('cutscript_profiles').select('display_name').eq('user_id',user.id).maybeSingle().then(({data,error:dbError})=>{
     if(!active)return;
     if(dbError)setError('Could not load your profile.');else setName(data?.display_name || user.user_metadata?.full_name || '');
   });
   return()=>{active=false;};
 },[user?.id,user?.user_metadata?.full_name]);
 async function refreshBilling(){
   if(!session?.access_token){setLoading(false);return;}
   setLoading(true);setError('');
   try{
     const response=await fetch('/api/billing/status',{headers:{Authorization:`Bearer ${session.access_token}`,'Cache-Control':'no-store'}});
     const result=await response.json().catch(()=>({}));
     if(!response.ok)throw new Error(result.error||'Could not load billing.');
     setBilling({...defaultBilling,...result});
   }catch(e){setError(e.message||'Could not load billing.');}
   finally{setLoading(false);}
 }
 useEffect(()=>{if(user?.id)void refreshBilling();},[user?.id,session?.access_token]);
 useEffect(()=>{
   const params=new URLSearchParams(window.location.search);
   const status=params.get('billing');
   if(status==='success'){setNotice('Checkout has returned. Your subscription will update after payment confirmation.');setTab('billing');}
   if(status==='cancel'){setNotice('Checkout canceled. No subscription was changed.');setTab('billing');}
   if(status==='return'){setNotice('Returned from billing management. Refresh to check for updates.');setTab('billing');}
   if(status && ['success','cancel','return'].includes(status)){
     params.delete('billing');
     window.history.replaceState({},'',window.location.pathname+(params.toString()?`?${params}`:'')+window.location.hash);
   }
 },[]);
 async function saveProfile(e){
   e.preventDefault();setError('');setNotice('');
   if(!user?.id)return;
   const clean=name.trim();
   if(clean.length>120){setError('Use a name under 120 characters.');return;}
   setSaving(true);
   try{
     const {error:dbError}=await supabase.from('cutscript_profiles').upsert({user_id:user.id,display_name:clean,updated_at:new Date().toISOString()},{onConflict:'user_id'});
     if(dbError)throw dbError;
     setNotice('Profile saved.');
   }catch{setError('Could not save the profile. Please retry.');}
   finally{setSaving(false);}
 }
 async function passwordReset(){
   setError('');setNotice('');
   try{await requestPasswordReset(user.email,`${window.location.origin}/#/reset-password`);setNotice('If email delivery is configured, a password reset link has been sent to your address.');}
   catch{setError('Could not send the password reset email. Check Supabase recovery redirect settings.');}
 }
 async function goToBilling(type){
   if(!billing.configured||busy)return;
   setBusy(true);setError('');setNotice('');
   try{
     const response=await fetch(`/api/billing/${type}`,{method:'POST',
       headers:{Authorization:`Bearer ${session.access_token}`,'Content-Type':'application/json'},body:'{}'});
     const result=await response.json().catch(()=>({}));
     if(!response.ok)throw new Error(result.error||'Billing is temporarily unavailable.');
     const url=new URL(result.url);
     if(url.protocol!=='https:' || !['checkout.stripe.com','billing.stripe.com'].includes(url.hostname))
       throw new Error('The billing link could not be verified.');
     window.location.assign(url.toString());
   }catch(e){setError(e.message);}
   finally{setBusy(false);}
 }
 const paid=['active','trialing','past_due','unpaid','incomplete'].includes(billing.status);
 const period=billing.currentPeriodEnd?new Date(billing.currentPeriodEnd):null;
 return <main className="cutscript-account-page">
   <div className="account-page-heading">
     <button className="account-back" type="button" onClick={onBack}><FiArrowLeft/> Back to projects</button>
     <h1>Account settings</h1>
     <p>Manage your CutScript account, security and subscription.</p>
   </div>
   {!user ? <div className="account-panel"><p>Sign in to manage your account.</p><button onClick={onSignIn}>Sign in</button></div> : <>
     <div className="account-layout">
       <nav className="account-tabs" aria-label="Account settings">
         <button aria-current={tab==='profile'?'page':undefined} onClick={()=>setTab('profile')}><FiUser/> Profile</button>
         <button aria-current={tab==='security'?'page':undefined} onClick={()=>setTab('security')}><FiShield/> Security</button>
         <button aria-current={tab==='billing'?'page':undefined} onClick={()=>setTab('billing')}><FiCreditCard/> Billing &amp; plans</button>
       </nav>
       <section className="account-panel" aria-live="polite">
         {error && <p className="account-error" role="alert"><FiAlertCircle/> {error}</p>}
         {notice && <p className="account-success" role="status"><FiCheckCircle/> {notice}</p>}
         {tab==='profile' && <>
           <div className="account-section-intro"><h2>Your profile</h2><p>Details used to identify you within CutScript.</p></div>
           <form className="account-form" onSubmit={saveProfile}>
             <label>Display name<input value={name} maxLength={120} autoComplete="name" onChange={e=>setName(e.target.value)} placeholder="Your name"/></label>
             <label>Email<input type="email" value={user.email||''} readOnly aria-describedby="account-email-help"/></label>
             <p id="account-email-help" className="account-hint">Your email is verified through Supabase Authentication. Contact support to change it during the beta.</p>
             <button className="account-primary" disabled={saving}>{saving?'Saving…':'Save changes'}</button>
           </form>
         </>}
         {tab==='security' && <>
           <div className="account-section-intro"><h2>Security</h2><p>Your password and sign-in are managed securely through Supabase.</p></div>
           <div className="account-detail-row"><div><strong>Sign-in email</strong><small>{user.email}</small></div><FiCheckCircle/></div>
           <div className="account-detail-row"><div><strong>Password</strong><small>Receive a secure link to change your password.</small></div>
             <button disabled={busy} onClick={passwordReset}>Send reset link</button></div>
           <p className="account-hint">Use a strong, unique password. Account deletion and email changes are not self-service during beta.</p>
         </>}
         {tab==='billing' && <>
           <div className="account-section-intro account-billing-heading"><div><h2>Billing &amp; plans</h2><p>Subscription, invoices and payment management.</p></div><button className="account-refresh" disabled={loading} onClick={refreshBilling}><FiRefreshCw/> Refresh</button></div>
           <div className="account-plan-card">
             <div><span className="account-eyebrow">CURRENT PLAN</span><h3>{billing.plan==='pro'?'CutScript Pro':'CutScript Beta'}</h3><p>{loading?'Checking subscription…':statuses[billing.status]||'Subscription status unavailable'}</p></div>
             <span className="account-plan-status">{billing.plan==='pro'?'Pro':'Beta access'}</span>
           </div>
           {period && !Number.isNaN(period.getTime()) && <div className="account-detail-row"><div><strong>{billing.cancelAtPeriodEnd?'Access until':'Current period ends'}</strong><small>{period.toLocaleDateString(undefined,{day:'numeric',month:'long',year:'numeric'})}</small></div></div>}
           {billing.cancelAtPeriodEnd && <p className="account-hint">Cancellation has been scheduled in Stripe. You retain access through your paid period.</p>}
           <div className="account-billing-actions">
             {billing.configured && !paid && <button className="account-primary" disabled={busy||loading} onClick={()=>goToBilling('checkout')}>{busy?'Opening…':'Upgrade to Pro'} <FiExternalLink/></button>}
             {billing.configured && billing.hasCustomer && <button disabled={busy||loading} onClick={()=>goToBilling('portal')}>Manage billing, invoices &amp; payment method <FiExternalLink/></button>}
           </div>
           {!billing.configured && <div className="account-billing-note"><strong>Billing is not accepting payments yet.</strong><p>Your beta access continues. Stripe subscriptions and self-service invoices will appear here when billing is activated.</p></div>}
           <p className="account-hint">Payments are handled by Stripe Checkout. Card details are never stored by CutScript. Any final price and applicable taxes will be shown before you confirm payment.</p>
         </>}
       </section>
     </div>
   </>}
 </main>;
}
